import type { Money } from "../../domain/money/money";
import type { IsoTimestamp } from "../../domain/time";
import type {
  LocalSale,
  LocalSaleLine,
  LocalTender,
  LocalDiscount,
} from "../../data/local/db";

/**
 * Receipt composition.
 *
 * THE RULE THAT MATTERS: a receipt is composed ONLY from values stored on the
 * sale. Nothing here reads current configuration, current prices, or the
 * current tax regime. That is what makes a reprint six months later reproduce
 * the original exactly after the item was renamed, repriced, retired, and the
 * business changed regime (FR-018, FR-024, FR-027c).
 *
 * The only exception is business IDENTITY — name, address, tax identifier —
 * which comes from BrandConfig because it belongs to the business rather than
 * to the transaction, and because confining it there is what makes a rebrand a
 * config change (Principle VI).
 */

export interface ReceiptBusiness {
  name: string;
  // Explicit `| undefined` because exactOptionalPropertyTypes is on: these are
  // populated from nullable database columns, so "present but undefined" is a
  // real state rather than a mistake.
  address?: string | undefined;
  contact?: string | undefined;
  taxIdentifier?: string | undefined;
  header?: string | undefined;
  footer?: string | undefined;
}

export interface ReceiptLineModifier {
  name: string;
  priceAdjustment: Money;
}

export interface ReceiptLine {
  lineNumber: number;
  name: string;
  variant: string | null;
  modifiers: ReceiptLineModifier[];
  note: string | null;
  quantity: number;
  unitPrice: Money;
  lineTotal: Money;
}

export interface ReceiptDiscount {
  type: string;
  label: string;
  cardholderName: string | null;
  cardholderId: string | null;
  amount: Money;
}

export interface ReceiptTender {
  type: string;
  amount: Money;
  reference: string | null;
  changeGiven: Money;
}

export interface ReceiptTax {
  regime: string;
  rateBp: number;
  taxable: Money;
  exempt: Money;
  zeroRated: Money;
  amount: Money;
}

export interface ReceiptDocument {
  business: ReceiptBusiness;
  documentNumber: string;
  issuedAt: IsoTimestamp;
  reprint: boolean;
  cashier: string;
  orderType: string;
  customerName: string | null;
  status: string;
  lines: ReceiptLine[];
  discounts: ReceiptDiscount[];
  /** Null under a non-VAT regime — no breakdown is shown (FR-027b). */
  tax: ReceiptTax | null;
  totals: {
    subtotal: Money;
    discountTotal: Money;
    total: Money;
  };
  tenders: ReceiptTender[];
  changeGiven: Money;
  /** Required and not removable through configuration (FR-034). */
  notice: string;
}

const DISCOUNT_LABELS: Record<string, string> = {
  senior_citizen: "Senior Citizen discount",
  pwd: "PWD discount",
  promotional: "Discount",
};

export interface ComposeInput {
  sale: LocalSale;
  lines: LocalSaleLine[];
  tenders: LocalTender[];
  discounts: LocalDiscount[];
  cashierName: string;
  business: ReceiptBusiness;
  complianceNotice: string;
  orderTypeLabels: Record<string, string>;
  reprint?: boolean;
}

export function composeReceipt(input: ComposeInput): ReceiptDocument {
  const { sale } = input;

  const lines: ReceiptLine[] = [...input.lines]
    .sort((a, b) => a.lineNumber - b.lineNumber)
    .map((line) => ({
      lineNumber: line.lineNumber,
      // Snapshots, not current catalog values.
      name: line.itemNameSnapshot,
      variant: line.variantNameSnapshot,
      modifiers: line.modifiers.map((modifier) => ({
        name: modifier.nameSnapshot,
        priceAdjustment: modifier.priceAdjustment,
      })),
      note: line.note,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      lineTotal: line.lineTotal,
    }));

  const discounts: ReceiptDiscount[] = input.discounts.map((discount) => ({
    type: discount.type,
    label: DISCOUNT_LABELS[discount.type] ?? "Discount",
    cardholderName: discount.cardholderName,
    cardholderId: discount.cardholderId,
    amount: discount.amount,
  }));

  const tenders: ReceiptTender[] = input.tenders.map((tender) => ({
    type: tender.type,
    amount: tender.amount,
    reference: tender.reference,
    changeGiven: tender.changeGiven,
  }));

  const changeGiven = tenders.reduce(
    (total, tender) => (total + tender.changeGiven) as Money,
    0 as Money,
  );

  return {
    business: input.business,
    documentNumber: sale.documentNumber,
    issuedAt: sale.createdAt,
    reprint: input.reprint ?? false,
    cashier: input.cashierName,
    orderType: input.orderTypeLabels[sale.orderType] ?? sale.orderType,
    customerName: sale.customerName,
    status: sale.status,
    lines,
    discounts,
    // The regime STORED ON THE SALE decides this, not the business's current
    // setting. A sale rung before VAT registration keeps its original form.
    tax:
      sale.taxRegime === "vat"
        ? {
            regime: sale.taxRegime,
            rateBp: sale.taxRateBp,
            taxable: sale.taxableAmount,
            exempt: sale.taxExemptAmount,
            zeroRated: sale.zeroRatedAmount,
            amount: sale.taxAmount,
          }
        : null,
    totals: {
      subtotal: sale.subtotal,
      discountTotal: sale.discountTotal,
      total: sale.total,
    },
    tenders,
    changeGiven,
    notice: input.complianceNotice,
  };
}
